/**
 * <LockedTabsTrigger /> — drop-in replacement for shadcn TabsTrigger that
 * degrades to a click-to-upgrade lookalike button when the current user is
 * on the Continuity System gateway tier.
 *
 * Why not just wrap TabsTrigger? Radix Tabs binds the click at the primitive
 * level, so wrapping is brittle. Substituting a visually-identical
 * <button> in the locked case keeps the tab bar layout intact while
 * cleanly diverting the click to the upgrade modal.
 *
 * Usage:
 *   <LockedTabsTrigger value="allocation" featureLabel="Asset Allocation"
 *                      className="..." title="...">
 *     Allocation
 *   </LockedTabsTrigger>
 */
import React from 'react';
import { TabsTrigger } from '@/components/ui/tabs';
import { Lock } from 'lucide-react';
import { useUserTier } from '../../hooks/useUserTier';
import { useUpgradeModal } from '../../contexts/UpgradeModalContext';

// Matches the base classes shadcn applies to TabsTrigger so the locked
// lookalike sits pixel-flush inside the same TabsList row.
const BASE_TRIGGER_CLS =
  'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

export function LockedTabsTrigger({
  value,
  featureLabel,
  children,
  className = '',
  title,
  'data-testid': testId,
  ...rest
}) {
  const { isGatewayContinuity, loading } = useUserTier();
  const { openUpgradeModal } = useUpgradeModal();

  if (loading || !isGatewayContinuity) {
    return (
      <TabsTrigger
        value={value}
        className={className}
        title={title}
        data-testid={testId}
        {...rest}
      >
        {children}
      </TabsTrigger>
    );
  }

  const lockedTitle = `${featureLabel} — available on Portfolio Clarity. Click to see how to unlock.`;
  const lockedTestId = testId ? `${testId}-locked` : undefined;

  return (
    <button
      type="button"
      onClick={() => openUpgradeModal(featureLabel)}
      title={lockedTitle}
      data-testid={lockedTestId}
      className={`${BASE_TRIGGER_CLS} ${className} opacity-60 hover:opacity-80 cursor-pointer`}
    >
      <span className="pointer-events-none">{children}</span>
      <Lock className="w-3 h-3 ml-1 text-slate-500" aria-label="Locked" />
    </button>
  );
}

export default LockedTabsTrigger;
